import { Lock } from 'lucide-react';
import { useAccountSalaryKey } from '@/hooks/payroll/useAccountSalaryKey';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  children: React.ReactNode;
  /** What the lock hides, for the locked card's wording. The same PIN unlocks it either way. */
  title?: string;
  description?: string;
}

/**
 * Hides an employee's own salary surface until their personal pass key is entered.
 *
 * The sibling of `SalaryGate`, which guards the admin payroll screens with the shared
 * key. They are kept apart on purpose: the two locks use different credentials stored
 * in different slots, so unlocking one never unlocks the other — an admin who has
 * opened the payroll screens still has to enter their own key to see their own payslip.
 */
export function AccountSalaryGate({
  children,
  title = 'Your salary is locked',
  description = 'Enter your personal salary pass key to view it.',
}: Props) {
  const { unlocked, requestUnlock } = useAccountSalaryKey();

  if (unlocked) return <>{children}</>;

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="rounded-full bg-muted p-4">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Button size="sm" onClick={requestUnlock}>Enter pass key</Button>
      </CardContent>
    </Card>
  );
}
